// src/modules/StructuredDS.jsx
// Complete data science pipeline: upload → clean → explore → visualize → Q&A
// All operations are REAL — cleaning actually mutates data, stats are computed

import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ScatterChart, Scatter,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  parseCSV, inferTypes, getStats, getHistogram, getCorrelation, getValueCounts,
  generateInsights, dropNullRows, fillWithMean, fillWithMedian, fillWithMode,
  removeDuplicates, removeOutliers, normalizeColumn, standardizeColumn,
} from '../../utils/csv.js';
import { Panel, Metric, Tag, LogPanel, Spinner, Tabs, InfoBox, Select, timestamp } from '../../components/ui.jsx';

const PALETTE = ['var(--cyan)', 'var(--green)', 'var(--violet)', 'var(--gold)', 'var(--red)', 'var(--orange)', 'var(--blue)', '#f472b6'];

const DSTabBar = ({ active, onChange, dataset }) => {
  const TABS = [
    { id: 'upload', label: 'UPLOAD', icon: '⬆' },
    { id: 'overview', label: 'OVERVIEW', icon: '◈', requiresData: true },
    { id: 'clean', label: 'CLEAN', icon: '◎', requiresData: true },
    { id: 'distribution', label: 'DISTRIBUTION', icon: '⬟', requiresData: true },
    { id: 'correlation', label: 'CORRELATION', icon: '⬡', requiresData: true },
    { id: 'visualize', label: 'VISUALIZE', icon: '◉', requiresData: true },
    { id: 'insights', label: 'Q&A', icon: '⬢', requiresData: true },
  ];
  return <Tabs tabs={TABS} active={active} onChange={onChange} dataset={dataset} />;
};

export default function StructuredDS({ mode }) {
  // ── State ──
  const [dataset, setDataset] = useState(null);     // { headers, rows, shape }
  const [colTypes, setColTypes] = useState([]);
  const [tab, setTab] = useState('upload');
  const [logs, setLogs] = useState([{ ts: '00:00:00', msg: 'Structured Data Science Lab ready.', t: 'info' }]);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [insights, setInsights] = useState([]);

  // Dist tab
  const [selCol, setSelCol] = useState('');
  const [bins, setBins] = useState(15);

  // Corr tab
  const [xCol, setXCol] = useState('');
  const [yCol, setYCol] = useState('');
  const [corrMatrix, setCorrMatrix] = useState({ matrix: [], cols: [] });

  // Viz tab
  const [chartType, setChartType] = useState('bar');
  const [vizX, setVizX] = useState('');
  const [vizY, setVizY] = useState('');

  // Q&A tab
  const [question, setQuestion] = useState('');

  const fileRef = useRef();
  const log = (msg, t = 'info') => setLogs(l => [...l.slice(-60), { ts: timestamp(), msg, t }]);

  // ── Load CSV ──
  const loadData = (text, name) => {
    setProcessing(true);
    log(`Parsing "${name}"...`);
    setTimeout(() => {
      const parsed = parseCSV(text);
      if (!parsed) { log('Failed to parse — check CSV format', 'err'); setProcessing(false); return; }
      const types = inferTypes(parsed.headers, parsed.rows);
      const nc = types.filter(t => t.type === 'numeric');
      const cc = types.filter(t => t.type === 'categorical');

      setDataset(parsed);
      setColTypes(types);
      setSelCol(nc[0]?.col || cc[0]?.col || parsed.headers[0]);
      setXCol(nc[0]?.col || '');
      setYCol(nc[1]?.col || nc[0]?.col || '');
      setVizX(nc[0]?.col || parsed.headers[0]);
      setVizY(nc[1]?.col || nc[0]?.col || '');

      // Correlation matrix
      const corrCols = nc.slice(0, 6).map(c => c.col);
      const matrix = corrCols.map(c1 => {
        const row = { col: c1 };
        corrCols.forEach(c2 => { row[c2] = getCorrelation(parsed.rows, c1, c2); });
        return row;
      });
      setCorrMatrix({ matrix, cols: corrCols });

      const ins = generateInsights(parsed, types);
      setInsights(ins);
      const nullTotal = types.reduce((s, t) => s + t.nullCount, 0);
      log(`✓ Loaded ${parsed.shape[0]} rows × ${parsed.shape[1]} cols`, 'ok');
      log(`${nc.length} numeric | ${cc.length} categorical | ${nullTotal} nulls`);
      setTab('overview');
      setProcessing(false);
    }, 350);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      log('Only .csv files supported', 'err'); return;
    }
    const reader = new FileReader();
    reader.onload = e => loadData(e.target.result, file.name);
    reader.readAsText(file);
  };

  const generateMock = (name, nRows, nCols) => {
    const cols = ['id', 'value', 'price', 'quantity', 'score', 'rating', 'weight', 'age', 'category', 'label', 'group', 'type'].slice(0, nCols);
    const cats = ['A', 'B', 'C', 'D', 'E'];
    const rows = Array.from({ length: nRows }, (_, i) => {
      const obj = {};
      cols.forEach((c, ci) => {
        if (ci >= cols.length - 3) { obj[c] = cats[Math.floor(Math.random() * cats.length)]; }
        else {
          const base = (ci + 1) * 20 + Math.random() * 50;
          // Inject ~5% nulls
          obj[c] = Math.random() < 0.05 ? '' : (base + (Math.random() - 0.5) * base).toFixed(2);
        }
      });
      return obj;
    });
    const csv = [cols.join(','), ...rows.map(r => cols.map(c => r[c]).join(','))].join('\n');
    loadData(csv, name + '.csv');
  };

  // ── Cleaning operations (REAL — updates dataset) ──
  const applyClean = (opName) => {
    if (!dataset) return;
    let result;
    switch (opName) {
      case 'drop_nulls':    result = dropNullRows(dataset.rows, dataset.headers); break;
      case 'fill_mean':     result = fillWithMean(dataset.rows, colTypes); break;
      case 'fill_median':   result = fillWithMedian(dataset.rows, colTypes); break;
      case 'fill_mode':     result = fillWithMode(dataset.rows, colTypes); break;
      case 'dedup':         result = removeDuplicates(dataset.rows); break;
      default: return;
    }
    const newDataset = { ...dataset, rows: result.rows, shape: [result.rows.length, dataset.headers.length] };
    const newTypes = inferTypes(newDataset.headers, newDataset.rows);
    setDataset(newDataset);
    setColTypes(newTypes);
    // Recompute corr matrix
    const nc = newTypes.filter(t => t.type === 'numeric');
    const corrCols = nc.slice(0, 6).map(c => c.col);
    const matrix = corrCols.map(c1 => {
      const row = { col: c1 };
      corrCols.forEach(c2 => { row[c2] = getCorrelation(newDataset.rows, c1, c2); });
      return row;
    });
    setCorrMatrix({ matrix, cols: corrCols });
    log(result.log, 'ok');
  };

  const applyColumnOp = (opName, col) => {
    if (!dataset || !col) return;
    let result;
    switch (opName) {
      case 'remove_outliers': result = removeOutliers(dataset.rows, col); break;
      case 'normalize':       result = normalizeColumn(dataset.rows, col); break;
      case 'standardize':     result = standardizeColumn(dataset.rows, col); break;
      default: return;
    }
    const newDataset = { ...dataset, rows: result.rows, shape: [result.rows.length, dataset.headers.length] };
    setDataset(newDataset);
    setColTypes(inferTypes(newDataset.headers, newDataset.rows));
    log(result.log, 'ok');
  };

  // ── Derived data ──
  const nc = colTypes.filter(c => c.type === 'numeric').map(c => c.col);
  const cc = colTypes.filter(c => c.type === 'categorical').map(c => c.col);
  const selColType = colTypes.find(c => c.col === selCol);
  const histData = dataset && selCol
    ? selColType?.type === 'numeric'
      ? getHistogram(dataset.rows, selCol, bins)
      : getValueCounts(dataset.rows, selCol)
    : [];
  const scatterData = dataset && xCol && yCol
    ? dataset.rows.slice(0, 500).map(r => ({ x: parseFloat(r[xCol]) || 0, y: parseFloat(r[yCol]) || 0 })).filter(d => !isNaN(d.x))
    : [];
  const nullTotal = colTypes.reduce((s, c) => s + c.nullCount, 0);
  const dupCount = dataset ? dataset.rows.length - new Set(dataset.rows.map(r => JSON.stringify(r))).size : 0;

  // ── Q&A ──
  const answerQ = () => {
    if (!question.trim() || !dataset) return;
    const q = question.toLowerCase();
    let ans = 'Try: "how many rows?", "mean of [col]", "max of [col]", "correlation", "null values", "unique in [col]", "shape"';

    if (q.match(/how many rows|row count/))      ans = `${dataset.rows.length} rows.`;
    else if (q.match(/column|feature/))           ans = `${dataset.headers.length} columns: ${dataset.headers.join(', ')}`;
    else if (q.match(/null|miss/))                ans = `${nullTotal} null values. ${colTypes.filter(c => c.nullCount > 0).map(c => `${c.col}(${c.nullCount})`).join(', ') || 'None'}`;
    else if (q.match(/dup/))                      ans = `${dupCount} duplicate rows.`;
    else if (q.match(/shape|size/))               ans = `Shape: ${dataset.shape[0]} × ${dataset.shape[1]}`;
    else if (q.match(/mean|average/)) {
      const col = nc.find(c => q.includes(c.toLowerCase())) || nc[0];
      if (col) { const s = getStats(dataset.rows, col); ans = s ? `Mean of "${col}": ${s.mean.toFixed(4)}, std: ${s.std.toFixed(4)}` : 'No data'; }
    }
    else if (q.match(/\bmax\b/)) {
      const col = nc.find(c => q.includes(c.toLowerCase())) || nc[0];
      if (col) { const s = getStats(dataset.rows, col); ans = s ? `Max of "${col}": ${s.max}` : 'No data'; }
    }
    else if (q.match(/\bmin\b/)) {
      const col = nc.find(c => q.includes(c.toLowerCase())) || nc[0];
      if (col) { const s = getStats(dataset.rows, col); ans = s ? `Min of "${col}": ${s.min}` : 'No data'; }
    }
    else if (q.match(/median/)) {
      const col = nc.find(c => q.includes(c.toLowerCase())) || nc[0];
      if (col) { const s = getStats(dataset.rows, col); ans = s ? `Median of "${col}": ${s.median}` : 'No data'; }
    }
    else if (q.match(/std|deviation/)) {
      const col = nc.find(c => q.includes(c.toLowerCase())) || nc[0];
      if (col) { const s = getStats(dataset.rows, col); ans = s ? `Std of "${col}": ${s.std.toFixed(4)}` : 'No data'; }
    }
    else if (q.match(/correl/)) {
      if (nc.length >= 2) { const c = getCorrelation(dataset.rows, nc[0], nc[1]); ans = `r(${nc[0]}, ${nc[1]}) = ${c}`; }
    }
    else if (q.match(/unique/)) {
      const col = dataset.headers.find(h => q.includes(h.toLowerCase()));
      if (col) { const ct = colTypes.find(c => c.col === col); ans = `"${col}" has ${ct?.uniqueCount} unique values.`; }
      else ans = `Unique counts: ${colTypes.slice(0, 5).map(c => `${c.col}=${c.uniqueCount}`).join(', ')}`;
    }
    else if (q.match(/skew/)) {
      const col = nc.find(c => q.includes(c.toLowerCase())) || nc[0];
      if (col) { const s = getStats(dataset.rows, col); ans = s ? `Skewness of "${col}": ${s.skew.toFixed(3)}` : 'No data'; }
    }

    log(`Q: ${question}`);
    log(`A: ${ans}`, 'ok');
    setQuestion('');
  };

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <DSTabBar active={tab} onChange={setTab} dataset={dataset} />

      {/* ══ UPLOAD ══ */}
      {tab === 'upload' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${isDragging ? 'var(--cyan)' : 'var(--b1)'}`,
              borderRadius: '4px', padding: '52px 24px', textAlign: 'center',
              cursor: 'pointer', background: isDragging ? 'var(--gcyan)' : 'var(--surface)',
              transition: 'all .2s', animation: 'float 3s ease infinite',
            }}
          >
            <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} />
            {processing
              ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <Spinner size={24} /><span style={{ color: 'var(--cyan)', fontSize: '12px' }}>PARSING DATA...</span>
                </div>
              : <>
                  <div style={{ fontSize: '40px', marginBottom: '14px', opacity: .4 }}>⬆</div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '18px', color: isDragging ? 'var(--cyan)' : 'var(--t1)', marginBottom: '7px' }}>
                    Drop CSV File Here
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '18px' }}>or click to browse your files</div>
                  <div style={{ padding: '7px 18px', border: '1px solid var(--b1)', borderRadius: '2px', display: 'inline-block', fontSize: '10px', color: 'var(--t2)', letterSpacing: '.1em' }}>
                    SUPPORTS .CSV · ANY SIZE
                  </div>
                </>
            }
          </div>

          <Panel title="SAMPLE DATASETS" accent="var(--cyan)">
            {[
              ['Iris Classification', 150, 8, 'Multi-class, clean'],
              ['Housing Prices', 506, 9, 'Regression, mixed types'],
              ['Titanic Survival', 891, 8, 'Binary, missing data ~20%'],
              ['E-Commerce Sales', 1200, 7, 'Time series, categories'],
              ['Student Scores', 300, 5, 'Simple numeric, few nulls'],
            ].map(([name, rows, cols, desc]) => (
              <div key={name} onClick={() => generateMock(name, rows, cols)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--b0)', cursor: 'pointer', transition: 'opacity .15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <div style={{ width: '36px', height: '36px', background: 'var(--gcyan)', border: '1px solid var(--cyan)33', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: 'var(--cyan)', flexShrink: 0 }}>⬟</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{rows} rows · {cols} cols · {desc}</div>
                </div>
                <span style={{ color: 'var(--cyan)', fontSize: '12px' }}>→</span>
              </div>
            ))}
          </Panel>

          <div style={{ gridColumn: '1/-1' }}>
            <Panel title="LOG"><LogPanel logs={logs} height={90} /></Panel>
          </div>
        </div>
      )}

      {/* ══ OVERVIEW ══ */}
      {tab === 'overview' && dataset && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
            <Metric label="Rows" value={dataset.shape[0].toLocaleString()} color="var(--cyan)" />
            <Metric label="Columns" value={dataset.shape[1]} color="var(--green)" />
            <Metric label="Numeric Cols" value={nc.length} color="var(--violet)" sub={nc.slice(0,3).join(', ')} />
            <Metric label="Categorical" value={cc.length} color="var(--gold)" sub={cc.slice(0,3).join(', ')} />
          </div>

          <Panel title="COLUMN MANIFEST" badge={`${colTypes.length} columns`}>
            <div style={{ maxHeight: '320px', overflowY: 'auto', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px' }}>
                <thead>
                  <tr style={{ background: 'var(--deep)' }}>
                    {['Column', 'Type', 'Non-Null', 'Null%', 'Unique', 'Sample Values'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', letterSpacing: '.1em', fontWeight: 500, borderBottom: '1px solid var(--b0)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {colTypes.map((ct, i) => {
                    const nn = dataset.rows.length - ct.nullCount;
                    const np = ((ct.nullCount / dataset.rows.length) * 100).toFixed(1);
                    const sample = [...new Set(dataset.rows.slice(0, 20).map(r => r[ct.col]).filter(Boolean))].slice(0, 3).join(', ');
                    return (
                      <tr key={ct.col} style={{ borderBottom: '1px solid var(--b0)', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,.12)' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--t1)', fontWeight: 500 }}>{ct.col}</td>
                        <td style={{ padding: '8px 12px' }}><Tag color={ct.type === 'numeric' ? 'var(--cyan)' : 'var(--gold)'}>{ct.type}</Tag></td>
                        <td style={{ padding: '8px 12px', color: 'var(--t2)' }}>{nn}</td>
                        <td style={{ padding: '8px 12px', color: parseFloat(np) > 10 ? 'var(--red)' : parseFloat(np) > 0 ? 'var(--gold)' : 'var(--green)' }}>{np}%</td>
                        <td style={{ padding: '8px 12px', color: 'var(--t2)' }}>{ct.uniqueCount}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--t3)', fontSize: '11px' }}>{sample}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="DATA PREVIEW" badge="first 10 rows">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '11px', minWidth: '600px' }}>
                <thead>
                  <tr style={{ background: 'var(--deep)' }}>
                    {dataset.headers.map(h => <th key={h} style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--cyan)', fontSize: '10px', letterSpacing: '.07em', borderBottom: '1px solid var(--b0)', whiteSpace: 'nowrap' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {dataset.rows.slice(0, 10).map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--b0)' }}>
                      {dataset.headers.map(h => (
                        <td key={h} style={{ padding: '6px 12px', color: 'var(--t2)', whiteSpace: 'nowrap' }}>
                          {row[h] === '' || row[h] == null ? <span style={{ color: 'var(--red)' }}>null</span> : row[h]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {/* ══ CLEAN ══ */}
      {tab === 'clean' && dataset && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          {/* Quality report */}
          <Panel title="DATA QUALITY REPORT" accent="var(--gold)">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <Metric label="Total Nulls" value={nullTotal} color={nullTotal > 0 ? 'var(--gold)' : 'var(--green)'} sub={`${((nullTotal / (dataset.rows.length * dataset.headers.length)) * 100).toFixed(2)}% of data`} />
              <Metric label="Duplicates" value={dupCount} color={dupCount > 0 ? 'var(--red)' : 'var(--green)'} />
            </div>

            <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em', marginBottom: '8px' }}>NULL RATE PER COLUMN</div>
            {colTypes.filter(c => c.nullCount > 0).length === 0
              ? <InfoBox color="var(--green)" icon="✓">No missing values. Dataset is complete.</InfoBox>
              : colTypes.filter(c => c.nullCount > 0).map(ct => (
                <div key={ct.col} style={{ marginBottom: '11px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--t1)' }}>{ct.col}</span>
                    <span style={{ fontSize: '11px', color: 'var(--gold)' }}>{ct.nullCount} ({((ct.nullCount / dataset.rows.length) * 100).toFixed(1)}%)</span>
                  </div>
                  <div style={{ height: '4px', background: 'var(--b0)', borderRadius: '2px' }}>
                    <div style={{ height: '100%', width: `${(ct.nullCount / dataset.rows.length) * 100}%`, background: 'var(--gold)', borderRadius: '2px' }} />
                  </div>
                </div>
              ))
            }
          </Panel>

          {/* Cleaning actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="GLOBAL CLEANING ACTIONS">
              {[
                { op: 'drop_nulls', label: 'Drop rows with nulls', color: 'var(--red)', desc: `Removes rows containing any null. Currently: ${colTypes.filter(c => c.nullCount > 0).length} cols affected` },
                { op: 'fill_mean', label: 'Fill numeric nulls → mean', color: 'var(--cyan)', desc: 'Imputes each numeric column with its mean' },
                { op: 'fill_median', label: 'Fill numeric nulls → median', color: 'var(--green)', desc: 'More robust to outliers than mean' },
                { op: 'fill_mode', label: 'Fill categorical nulls → mode', color: 'var(--violet)', desc: 'Most frequent value per categorical column' },
                { op: 'dedup', label: 'Remove duplicate rows', color: 'var(--gold)', desc: `Exact-match deduplication. Currently: ${dupCount} duplicates` },
              ].map(({ op, label, color, desc }) => (
                <div key={op} onClick={() => applyClean(op)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', border: '1px solid var(--b0)', borderRadius: '3px', marginBottom: '6px', cursor: 'pointer', background: 'var(--raised)', transition: 'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = 'var(--hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--b0)'; e.currentTarget.style.background = 'var(--raised)'; }}
                >
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 500 }}>{label}</div>
                    <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{desc}</div>
                  </div>
                  <span style={{ fontSize: '11px', color }}>APPLY →</span>
                </div>
              ))}
            </Panel>

            <Panel title="LOG"><LogPanel logs={logs} height={90} /></Panel>
          </div>

          {/* Column transforms */}
          <div style={{ gridColumn: '1/-1' }}>
            <Panel title="COLUMN TRANSFORMS">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                {nc.slice(0, 6).map(col => {
                  const stats = getStats(dataset.rows, col);
                  const vals = dataset.rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
                  const sorted = [...vals].sort((a,b)=>a-b);
                  const q1 = sorted[Math.floor(sorted.length * 0.25)] || 0;
                  const q3 = sorted[Math.floor(sorted.length * 0.75)] || 0;
                  const iqr = q3 - q1;
                  const outlierCount = vals.filter(v => v < q1 - 1.5*iqr || v > q3 + 1.5*iqr).length;
                  return (
                    <div key={col} style={{ padding: '12px', background: 'var(--raised)', border: '1px solid var(--b0)', borderRadius: '3px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 600, marginBottom: '8px' }}>{col}</div>
                      {stats && <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '8px' }}>
                        μ={stats.mean.toFixed(2)} σ={stats.std.toFixed(2)} · {outlierCount} outliers
                      </div>}
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {[
                          { op: 'remove_outliers', label: 'Remove outliers', color: 'var(--gold)' },
                          { op: 'normalize', label: 'Normalize [0,1]', color: 'var(--cyan)' },
                          { op: 'standardize', label: 'Z-score', color: 'var(--green)' },
                        ].map(({ op, label, color }) => (
                          <button key={op} onClick={() => applyColumnOp(op, col)}
                            style={{ padding: '4px 8px', background: 'transparent', border: `1px solid ${color}44`, color, fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.07em', transition: 'all .15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = color + '15'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >{label}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ DISTRIBUTION ══ */}
      {tab === 'distribution' && dataset && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '14px' }}>
          {/* Column selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="SELECT COLUMN">
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {colTypes.map(ct => (
                  <div key={ct.col} onClick={() => setSelCol(ct.col)}
                    style={{ padding: '9px 10px', cursor: 'pointer', borderRadius: '2px', background: selCol === ct.col ? 'var(--gcyan)' : 'transparent', border: `1px solid ${selCol === ct.col ? 'var(--cyan)44' : 'transparent'}`, marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: ct.type === 'numeric' ? 'var(--cyan)' : 'var(--gold)', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: selCol === ct.col ? 'var(--cyan)' : 'var(--t1)', fontWeight: selCol === ct.col ? 600 : 400 }}>{ct.col}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '9px', color: 'var(--t3)' }}>{ct.type === 'numeric' ? 'num' : 'cat'}</span>
                  </div>
                ))}
              </div>
            </Panel>

            {selCol && (() => {
              const stats = selColType?.type === 'numeric' ? getStats(dataset.rows, selCol) : null;
              return (
                <Panel title="STATISTICS">
                  {stats
                    ? <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px' }}>
                        {[['Mean', stats.mean.toFixed(3)], ['Std', stats.std.toFixed(3)], ['Min', stats.min], ['Max', stats.max], ['Q1', stats.q1], ['Median', stats.median], ['Q3', stats.q3], ['Skew', stats.skew.toFixed(2)], ['Count', stats.count]].map(([k, v]) => (
                          <div key={k} style={{ background: 'var(--raised)', padding: '8px', borderRadius: '2px' }}>
                            <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.08em' }}>{k}</div>
                            <div style={{ fontSize: '13px', color: 'var(--cyan)', fontWeight: 600, fontFamily: 'Syne' }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    : <div>
                        {getValueCounts(dataset.rows, selCol, 8).map(({ value, count, pct }) => (
                          <div key={value} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--b0)' }}>
                            <span style={{ fontSize: '11px', color: 'var(--t1)' }}>{value}</span>
                            <span style={{ fontSize: '11px', color: 'var(--gold)', fontWeight: 600 }}>{count} <span style={{ color: 'var(--t3)', fontWeight: 400 }}>({pct}%)</span></span>
                          </div>
                        ))}
                      </div>
                  }
                  {selColType?.type === 'numeric' && mode !== 'BEGINNER' && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '6px' }}>HISTOGRAM BINS: {bins}</div>
                      <input type="range" min={5} max={50} step={1} value={bins} onChange={e => setBins(parseInt(e.target.value))}
                        style={{ width: '100%', accentColor: 'var(--cyan)' }} />
                    </div>
                  )}
                </Panel>
              );
            })()}
          </div>

          {/* Charts */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title={selColType?.type === 'numeric' ? 'HISTOGRAM' : 'VALUE COUNTS'} badge={selCol} accent="var(--violet)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={histData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey={selColType?.type === 'numeric' ? 'range' : 'value'} stroke="var(--t3)" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {histData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={0.85} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            {selColType?.type === 'numeric' && (() => {
              const stats = getStats(dataset.rows, selCol);
              if (!stats) return null;
              const range = stats.max - stats.min || 1;
              const p = v => ((v - stats.min) / range) * 100;
              return (
                <Panel title="BOX PLOT" accent="var(--cyan)">
                  <div style={{ padding: '24px 20px 12px' }}>
                    <div style={{ height: '44px', position: 'relative' }}>
                      {/* Left whisker */}
                      <div style={{ position: 'absolute', left: `${p(stats.min)}%`, width: `${p(stats.q1) - p(stats.min)}%`, height: '2px', background: 'var(--t3)', top: '50%', transform: 'translateY(-50%)' }} />
                      {/* Right whisker */}
                      <div style={{ position: 'absolute', left: `${p(stats.q3)}%`, width: `${p(stats.max) - p(stats.q3)}%`, height: '2px', background: 'var(--t3)', top: '50%', transform: 'translateY(-50%)' }} />
                      {/* Min/max ticks */}
                      <div style={{ position: 'absolute', left: `${p(stats.min)}%`, width: '2px', height: '16px', background: 'var(--t3)', top: '50%', transform: 'translateY(-50%)' }} />
                      <div style={{ position: 'absolute', left: `${p(stats.max)}%`, width: '2px', height: '16px', background: 'var(--t3)', top: '50%', transform: 'translateY(-50%)' }} />
                      {/* IQR Box */}
                      <div style={{ position: 'absolute', left: `${p(stats.q1)}%`, width: `${p(stats.q3) - p(stats.q1)}%`, height: '32px', top: '50%', transform: 'translateY(-50%)', background: 'var(--gcyan)', border: '1px solid var(--cyan)', borderRadius: '2px' }} />
                      {/* Median line */}
                      <div style={{ position: 'absolute', left: `${p(stats.median)}%`, width: '2px', height: '32px', top: '50%', transform: 'translateY(-50%)', background: 'var(--red)', zIndex: 1 }} />
                      {/* Mean dot */}
                      <div style={{ position: 'absolute', left: `${p(stats.mean)}%`, width: '10px', height: '10px', borderRadius: '50%', background: 'var(--gold)', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 2 }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--t3)', marginTop: '8px' }}>
                      <span>{stats.min.toFixed(2)}</span>
                      <span style={{ color: 'var(--cyan)' }}>Q1: {stats.q1.toFixed(2)}</span>
                      <span style={{ color: 'var(--red)' }}>Median: {stats.median.toFixed(2)}</span>
                      <span style={{ color: 'var(--cyan)' }}>Q3: {stats.q3.toFixed(2)}</span>
                      <span>{stats.max.toFixed(2)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '14px', padding: '0 4px 4px', fontSize: '10px' }}>
                    <span style={{ color: 'var(--red)' }}>━ median</span>
                    <span style={{ color: 'var(--gold)' }}>● mean</span>
                    <span style={{ color: 'var(--cyan)' }}>□ IQR (Q1–Q3)</span>
                    <span style={{ color: 'var(--t3)' }}>── whiskers (min/max)</span>
                  </div>
                </Panel>
              );
            })()}
          </div>
        </div>
      )}

      {/* ══ CORRELATION ══ */}
      {tab === 'correlation' && dataset && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '14px' }}>
          <Panel title="CORRELATION HEATMAP" badge={`${corrMatrix.cols.length} numeric cols`} accent="var(--green)">
            {corrMatrix.matrix.length > 0
              ? <div style={{ overflowX: 'auto' }}>
                  <div style={{ display: 'inline-block' }}>
                    <div style={{ display: 'flex', gap: '2px', marginLeft: '92px', marginBottom: '3px' }}>
                      {corrMatrix.cols.map(c => <div key={c} style={{ width: '72px', fontSize: '9px', color: 'var(--t3)', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 2px' }}>{c}</div>)}
                    </div>
                    {corrMatrix.matrix.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: '2px', alignItems: 'center', marginBottom: '2px' }}>
                        <div style={{ width: '90px', fontSize: '9px', color: 'var(--t3)', textAlign: 'right', paddingRight: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.col}</div>
                        {corrMatrix.cols.map(c => {
                          const v = row[c]; const abs = Math.abs(v);
                          return (
                            <div key={c} style={{ width: '72px', height: '44px', borderRadius: '2px', background: v >= 0 ? `rgba(0,217,255,${abs * .85})` : `rgba(255,53,86,${abs * .85})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: abs > .6 ? 700 : 400, color: abs > .5 ? 'var(--void)' : 'var(--t2)', cursor: 'pointer' }}
                              title={`r(${row.col}, ${c}) = ${v}`}>
                              {v.toFixed(2)}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '10px' }}>
                    <span style={{ color: 'var(--cyan)' }}>■ positive correlation</span>
                    <span style={{ color: 'var(--red)' }}>■ negative correlation</span>
                    <span style={{ color: 'var(--t3)' }}>Intensity = |r|</span>
                  </div>
                </div>
              : <InfoBox>Need at least 2 numeric columns for correlation analysis.</InfoBox>
            }
          </Panel>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="TOP CORRELATIONS">
              {(() => {
                const pairs = [];
                corrMatrix.cols.forEach((c1, i) => {
                  corrMatrix.cols.slice(i + 1).forEach(c2 => {
                    const v = corrMatrix.matrix[i]?.[c2];
                    if (v !== undefined) pairs.push({ c1, c2, v });
                  });
                });
                pairs.sort((a, b) => Math.abs(b.v) - Math.abs(a.v));
                return pairs.slice(0, 8).map(({ c1, c2, v }) => (
                  <div key={`${c1}${c2}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderBottom: '1px solid var(--b0)' }}>
                    <div style={{ flex: 1, fontSize: '11px', color: 'var(--t1)' }}>{c1} ↔ {c2}</div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: Math.abs(v) > .7 ? 'var(--red)' : Math.abs(v) > .4 ? 'var(--gold)' : 'var(--t2)' }}>{v.toFixed(3)}</div>
                  </div>
                ));
              })()}
            </Panel>

            <Panel title="SCATTER EXPLORER">
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {[['X', xCol, setXCol], ['Y', yCol, setYCol]].map(([axis, val, setter]) => (
                  <div key={axis} style={{ flex: 1 }}>
                    <label style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em', display: 'block', marginBottom: '4px' }}>{axis}-AXIS</label>
                    <select value={val} onChange={e => setter(e.target.value)} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '6px 8px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '2px' }}>
                      {nc.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={190}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 9 }} name={xCol} />
                  <YAxis dataKey="y" stroke="var(--t3)" tick={{ fontSize: 9 }} name={yCol} />
                  <Tooltip />
                  <Scatter data={scatterData} fill="var(--cyan)" opacity={0.65} />
                </ScatterChart>
              </ResponsiveContainer>
              {xCol && yCol && <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '11px', color: 'var(--t2)' }}>
                r({xCol}, {yCol}) = <span style={{ color: 'var(--cyan)', fontWeight: 700 }}>{getCorrelation(dataset.rows, xCol, yCol)}</span>
              </div>}
            </Panel>
          </div>
        </div>
      )}

      {/* ══ VISUALIZE ══ */}
      {tab === 'visualize' && dataset && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '14px' }}>
          <Panel title="CHART BUILDER">
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em', marginBottom: '7px' }}>CHART TYPE</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                {[['bar', 'Bar'], ['line', 'Line'], ['area', 'Area'], ['pie', 'Pie'], ['scatter', 'Scatter'], ['histogram', 'Histogram']].map(([t, l]) => (
                  <button key={t} onClick={() => setChartType(t)}
                    style={{ padding: '8px 4px', border: `1px solid ${chartType === t ? 'var(--cyan)' : 'var(--b0)'}`, background: chartType === t ? 'var(--gcyan)' : 'var(--raised)', color: chartType === t ? 'var(--cyan)' : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {['bar', 'line', 'area', 'scatter'].includes(chartType) && (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em', display: 'block', marginBottom: '4px' }}>X-AXIS</label>
                  <select value={vizX} onChange={e => setVizX(e.target.value)} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '7px 8px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '2px' }}>
                    {dataset.headers.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em', display: 'block', marginBottom: '4px' }}>Y-AXIS (numeric)</label>
                  <select value={vizY} onChange={e => setVizY(e.target.value)} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '7px 8px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '2px' }}>
                    {nc.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </>
            )}
            {['pie', 'histogram'].includes(chartType) && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em', display: 'block', marginBottom: '4px' }}>COLUMN</label>
                <select value={selCol} onChange={e => setSelCol(e.target.value)} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '7px 8px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '2px' }}>
                  {colTypes.map(c => <option key={c.col} value={c.col}>{c.col}</option>)}
                </select>
              </div>
            )}
          </Panel>

          <Panel title="CHART OUTPUT" badge={chartType.toUpperCase()} accent="var(--violet)">
            {(() => {
              if (chartType === 'scatter') {
                const d = dataset.rows.slice(0, 400).map(r => ({ x: parseFloat(r[vizX]) || 0, y: parseFloat(r[vizY]) || 0 })).filter(d => !isNaN(d.x));
                return <ResponsiveContainer width="100%" height={400}><ScatterChart><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" /><XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 10 }} name={vizX} /><YAxis dataKey="y" stroke="var(--t3)" tick={{ fontSize: 10 }} name={vizY} /><Tooltip /><Scatter data={d} fill="var(--violet)" opacity={.7} /></ScatterChart></ResponsiveContainer>;
              }
              if (chartType === 'pie') {
                const d = getValueCounts(dataset.rows, selCol, 9);
                return <ResponsiveContainer width="100%" height={400}><PieChart><Pie data={d} dataKey="count" nameKey="value" cx="50%" cy="50%" outerRadius={150} label={({ value, percent }) => `${value} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: 'var(--t3)' }} fontSize={10}>{d.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>;
              }
              if (chartType === 'histogram') {
                const d = getHistogram(dataset.rows, selCol, bins);
                return <ResponsiveContainer width="100%" height={400}><BarChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" /><XAxis dataKey="range" stroke="var(--t3)" tick={{ fontSize: 10 }} interval="preserveStartEnd" /><YAxis stroke="var(--t3)" tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="count" radius={[2, 2, 0, 0]}>{d.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={.85} />)}</Bar></BarChart></ResponsiveContainer>;
              }
              const d = dataset.rows.slice(0, 60).map(r => ({ x: r[vizX], y: parseFloat(r[vizY]) || 0 }));
              if (chartType === 'line') return <ResponsiveContainer width="100%" height={400}><LineChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" /><XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 10 }} interval="preserveStartEnd" /><YAxis stroke="var(--t3)" tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="y" stroke="var(--violet)" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>;
              if (chartType === 'area') return <ResponsiveContainer width="100%" height={400}><AreaChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" /><XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 10 }} interval="preserveStartEnd" /><YAxis stroke="var(--t3)" tick={{ fontSize: 10 }} /><Tooltip /><Area type="monotone" dataKey="y" stroke="var(--violet)" fill="var(--violet)" fillOpacity={.15} strokeWidth={2} /></AreaChart></ResponsiveContainer>;
              return <ResponsiveContainer width="100%" height={400}><BarChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" /><XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 10 }} interval="preserveStartEnd" /><YAxis stroke="var(--t3)" tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="y" fill="var(--violet)" radius={[2, 2, 0, 0]} opacity={.85} /></BarChart></ResponsiveContainer>;
            })()}
          </Panel>
        </div>
      )}

      {/* ══ INSIGHTS & Q&A ══ */}
      {tab === 'insights' && dataset && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <Panel title="AUTO-GENERATED INSIGHTS" accent="var(--green)">
            {insights.map((ins, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', padding: '12px', background: 'var(--raised)', border: `1px solid ${ins.color}33`, borderRadius: '3px', marginBottom: '8px' }}>
                <span style={{ color: ins.color, fontSize: '20px', flexShrink: 0 }}>{ins.icon}</span>
                <div style={{ fontSize: '12px', color: 'var(--t1)', lineHeight: 1.65 }}>{ins.text}</div>
              </div>
            ))}
          </Panel>

          <Panel title="QUESTION & ANSWER ENGINE" accent="var(--cyan)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '12px' }}>Ask questions in plain English about your dataset.</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input value={question} onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && answerQ()}
                placeholder={`e.g. "Mean of ${nc[0] || 'column'}" or "any nulls?"`}
                style={{ flex: 1, padding: '10px 13px', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none' }}
              />
              <button onClick={answerQ}
                style={{ padding: '10px 18px', background: 'var(--gcyan)', border: '1px solid var(--cyan)', color: 'var(--cyan)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', letterSpacing: '.08em', fontWeight: 600 }}>
                ASK →
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
              {[`How many rows?`, `What columns?`, `Null values?`, nc[0] && `Mean of ${nc[0]}`, nc[0] && `Skewness of ${nc[0]}`, cc[0] && `Unique in ${cc[0]}`].filter(Boolean).map(q => (
                <button key={q} onClick={() => setQuestion(q)}
                  style={{ padding: '5px 11px', background: 'var(--raised)', border: '1px solid var(--b1)', color: 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>
                  {q}
                </button>
              ))}
            </div>

            <Panel title="ANSWER LOG"><LogPanel logs={logs.slice(-25)} height={200} /></Panel>
          </Panel>
        </div>
      )}
    </div>
  );
}
